import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient,
  type InfiniteData, type QueryClient,
} from '@tanstack/react-query';
import { apiFetch } from './client';
import type { Outfit } from '../pet3d/types';
import { surfacesKey } from './surfaces';
import { stripProjected, type EquippedSlot } from './wardrobe';
// Type-only, circular with profiles.ts (see that file's own note) — safe, erased at compile time.
import type { ProfileDto } from './profiles';

export type LookDto = {
  id: number;
  companionId: number;
  name: string;
  speciesCode: string;
  stage: string;
  slots: Record<string, EquippedSlot>;
  // What the Look looks like, for the 3D snapshot (W4): dyes already resolved to hex by the server.
  worn: Outfit;
  isPublished: boolean;
  isProfileDisplay: boolean;
  favourites: number;
  createdAt: string;
  // Task 19 widening: server/src/main/java/com/projectpets/looks/LookDto.java (package-private
  // record, verified against source) always carries these two — "owner sees own content flagged"
  // (R4), populated from moderation_state via ModerationGuard on every save/patch/publish/
  // unpublish/list call (LookService#toDto's own javadoc), falling back to a look's OWNER'S
  // profile-level hide when the look itself was never individually hidden. This DTO never reaches
  // a stranger (the public gallery uses GalleryLookDto instead, which excludes hidden looks
  // entirely), so `hidden`/`moderationReason` here are always about the CALLER'S OWN look.
  hidden: boolean;
  moderationReason: string | null;
};

export type GalleryLookDto = {
  lookId: number;
  name: string;
  favourites: number;
  favouritedByMe: boolean;
  worn: Outfit;
  speciesCode: string;
  stage: string;
  ownerDisplayName: string;
};

// 'following' added Phase 6 Task 16 — server: looks.LookService#gallery already accepted this
// value since Task 5 ("following additionally restricts to owners callerId follows -- always
// newest first"); the web side just never had a UI entry point for it until the gallery's own
// Following filter chip (LookGalleryScreen.tsx) landed.
export type GallerySort = 'newest' | 'top' | 'following';

export type GalleryPage = {
  looks: GalleryLookDto[];
  page: number;
  hasMore: boolean;
};

export const looksKeys = {
  all: ['looks'] as const,
  list: (companionId: number) => ['looks', companionId] as const,
};

// Broad ['gallery'] / ['looks'] keys below are deliberate: TanStack Query's invalidateQueries
// does prefix matching, so invalidating ['looks'] hits every ['looks', companionId] entry and
// invalidating ['gallery'] hits every ['gallery', sort] entry without the mutation needing to
// know which companion/sort is currently cached.
export const galleryKeys = {
  all: ['gallery'] as const,
  list: (sort: GallerySort) => ['gallery', sort] as const,
};

export const listLooks = (companionId: number) =>
  apiFetch<LookDto[]>(`/looks?companionId=${companionId}`);

// `stripProjected`: same wire rule as the outfit PUT — see its doc comment in api/wardrobe.ts.
export const saveLook = (input: { companionId: number; name: string; slots: Record<string, EquippedSlot> }) =>
  apiFetch<LookDto>('/looks', {
    method: 'POST',
    body: JSON.stringify({ ...input, slots: stripProjected(input.slots) }),
  });

export const patchLook = (input: { id: number; name?: string; isProfileDisplay?: boolean }) => {
  const { id, ...patch } = input;
  return apiFetch<LookDto>(`/looks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
};

export const deleteLook = (id: number) => apiFetch<void>(`/looks/${id}`, { method: 'DELETE' });

export const publishLook = (id: number) => apiFetch<LookDto>(`/looks/${id}/publish`, { method: 'POST' });

export const unpublishLook = (id: number) => apiFetch<LookDto>(`/looks/${id}/publish`, { method: 'DELETE' });

export const favouriteLook = (id: number) =>
  apiFetch<{ favourites: number }>(`/looks/${id}/favourite`, { method: 'POST' });

export const unfavouriteLook = (id: number) =>
  apiFetch<{ favourites: number }>(`/looks/${id}/favourite`, { method: 'DELETE' });

export const getGallery = (sort: GallerySort, page: number) =>
  apiFetch<GalleryPage>(`/looks/gallery?sort=${sort}&page=${page}`);

export function useLooks(companionId: number | undefined) {
  return useQuery({
    queryKey: looksKeys.list(companionId ?? -1),
    queryFn: () => listLooks(companionId as number),
    enabled: companionId != null,
  });
}

export function useSaveLook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveLook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: looksKeys.all });
      queryClient.invalidateQueries({ queryKey: galleryKeys.all });
    },
  });
}

export function usePatchLook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchLook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: looksKeys.all });
      queryClient.invalidateQueries({ queryKey: galleryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useDeleteLook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteLook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: looksKeys.all });
      queryClient.invalidateQueries({ queryKey: galleryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function usePublishLook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: publishLook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: surfacesKey });
      queryClient.invalidateQueries({ queryKey: looksKeys.all });
      queryClient.invalidateQueries({ queryKey: galleryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useUnpublishLook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: unpublishLook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: looksKeys.all });
      queryClient.invalidateQueries({ queryKey: galleryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useGallery(sort: GallerySort) {
  return useInfiniteQuery({
    queryKey: galleryKeys.list(sort),
    queryFn: ({ pageParam }) => getGallery(sort, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  });
}

type GalleryInfiniteData = InfiniteData<GalleryPage>;

// `nextFavourites` is a function of the look's *current* cached count rather than a fixed
// number: onMutate doesn't know the authoritative server count yet (only the optimistic +-1),
// while onSuccess does (the server's response) and overwrites it directly regardless of what's
// cached — see the two call sites below.
function walkGalleryCaches(
  queryClient: QueryClient,
  lookId: number,
  favouritedByMe: boolean,
  nextFavourites: (current: number) => number,
) {
  const matches = queryClient.getQueriesData<GalleryInfiniteData>({ queryKey: galleryKeys.all });
  for (const [key, data] of matches) {
    if (!data) continue;
    queryClient.setQueryData<GalleryInfiniteData>(key, {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        looks: page.looks.map((look) =>
          look.lookId === lookId
            ? { ...look, favouritedByMe, favourites: nextFavourites(look.favourites) }
            : look),
      })),
    });
  }
}

function walkProfileCaches(
  queryClient: QueryClient,
  lookId: number,
  favouritedByMe: boolean,
  nextFavourites: (current: number) => number,
) {
  const matches = queryClient.getQueriesData<ProfileDto>({ queryKey: ['profile'] });
  for (const [key, data] of matches) {
    if (!data?.publishedLook || data.publishedLook.lookId !== lookId) continue;
    queryClient.setQueryData<ProfileDto>(key, {
      ...data,
      publishedLook: {
        ...data.publishedLook,
        favouritedByMe,
        favourites: nextFavourites(data.publishedLook.favourites),
      },
    });
  }
}

type FavouriteContext = {
  previousGalleries: [readonly unknown[], GalleryInfiniteData | undefined][];
  previousProfiles: [readonly unknown[], ProfileDto | undefined][];
};

async function snapshotFavouriteCaches(queryClient: QueryClient): Promise<FavouriteContext> {
  await queryClient.cancelQueries({ queryKey: galleryKeys.all });
  await queryClient.cancelQueries({ queryKey: ['profile'] });
  return {
    previousGalleries: queryClient.getQueriesData<GalleryInfiniteData>({ queryKey: galleryKeys.all }),
    previousProfiles: queryClient.getQueriesData<ProfileDto>({ queryKey: ['profile'] }),
  };
}

function rollbackFavouriteCaches(queryClient: QueryClient, context: FavouriteContext | undefined) {
  context?.previousGalleries.forEach(([key, data]) => queryClient.setQueryData(key, data));
  context?.previousProfiles.forEach(([key, data]) => queryClient.setQueryData(key, data));
}

/**
 * Optimistic favourite: flips `favouritedByMe` and adjusts the count in every cached gallery
 * page and any profile whose `publishedLook` is this look, rolling back both on error.
 *
 * `onSuccess` overwrites the optimistic +-1 with the server's authoritative `favourites` count
 * (guards against drift if someone else favourited the same look in the meantime) and
 * `onSettled` then only invalidates on error — on success the cache is already correct, so no
 * extra refetch round-trip is needed.
 */
export function useFavouriteLook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lookId: number) => favouriteLook(lookId),
    onMutate: async (lookId) => {
      const context = await snapshotFavouriteCaches(queryClient);
      walkGalleryCaches(queryClient, lookId, true, (current) => current + 1);
      walkProfileCaches(queryClient, lookId, true, (current) => current + 1);
      return context;
    },
    onError: (_err, _lookId, context) => rollbackFavouriteCaches(queryClient, context),
    onSuccess: (data, lookId) => {
      walkGalleryCaches(queryClient, lookId, true, () => data.favourites);
      walkProfileCaches(queryClient, lookId, true, () => data.favourites);
    },
    onSettled: (_data, error) => {
      if (!error) return;
      queryClient.invalidateQueries({ queryKey: galleryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useUnfavouriteLook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lookId: number) => unfavouriteLook(lookId),
    onMutate: async (lookId) => {
      const context = await snapshotFavouriteCaches(queryClient);
      walkGalleryCaches(queryClient, lookId, false, (current) => current - 1);
      walkProfileCaches(queryClient, lookId, false, (current) => current - 1);
      return context;
    },
    onError: (_err, _lookId, context) => rollbackFavouriteCaches(queryClient, context),
    onSuccess: (data, lookId) => {
      walkGalleryCaches(queryClient, lookId, false, () => data.favourites);
      walkProfileCaches(queryClient, lookId, false, () => data.favourites);
    },
    onSettled: (_data, error) => {
      if (!error) return;
      queryClient.invalidateQueries({ queryKey: galleryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}
